import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./RecentsList.module.css";
import AnimatedButton2 from "../AnimatedButton/AnimatedButton2";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const authToken = () => localStorage.getItem("token") || "";

const fmtDuration = (s) => {
  if (!s) return "";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

// "Today" / "Yesterday" / "20 Aug"
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return "Today";
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return "Yesterday";

  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
};

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const STATUS_LABEL = {
  pickup: { text: "Answered", cls: "ok" },
  Notpickup: { text: "No answer", cls: "warn" },
  failed: { text: "Failed", cls: "bad" },
};

const LIVE_LABEL = { text: "In progress", cls: "live" };

// Itni der baad bina endTime wali row ko chalti hui call maanna band kar do.
const STALE_CALL_MS = 2 * 60 * 60 * 1000;

/*
 * Row dial karte hi ban jaati hai aur status "Notpickup" hota hai, kyunki tab
 * tak pata hi nahi ki uthegi ya nahi. Isliye chalti hui call doosre bande ko
 * "No answer" dikhti thi. Band hui call me endTime aata hai — wahi farq hai.
 */
const isInProgress = (call) => {
  if (call.endTime) return false;
  const started = new Date(call.startTime || call.createdAt).getTime();
  if (Number.isNaN(started)) return false;
  return Date.now() - started < STALE_CALL_MS;
};

// Recording sirf play dabane pe load hoti hai — auth header chahiye, isliye blob me fetch karte hain.
const RecordingButton = ({ callId }) => {
  const [state, setState] = useState("idle"); // idle | loading | playing | error
  const audioRef = useRef(null);

  useEffect(
    () => () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.pause();
      if (audio.src.startsWith("blob:")) URL.revokeObjectURL(audio.src);
    },
    []
  );

  const toggle = async (e) => {
    e.stopPropagation();

    if (state === "playing") {
      audioRef.current?.pause();
      setState("idle");
      return;
    }

    if (audioRef.current) {
      audioRef.current.play();
      setState("playing");
      return;
    }

    setState("loading");
    try {
      const res = await fetch(`${API_BASE}/dialer/calls/${callId}/recording`, {
        headers: { Authorization: `Bearer ${authToken()}` },
      });
      if (!res.ok) throw new Error("recording unavailable");
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audio.onended = () => setState("idle");
      audio.onerror = () => setState("error");
      audioRef.current = audio;
      await audio.play();
      setState("playing");
    } catch (_) {
      setState("error");
    }
  };

  return (
    <button
      type="button"
      className={`${styles.recBtn} ${state === "playing" ? styles.recPlaying : ""}`}
      onClick={toggle}
      disabled={state === "loading"}
      title={state === "error" ? "Recording not available" : "Play recording"}
      aria-label="Play recording"
    >
      {state === "loading" ? (
        <span className={styles.recSpinner} />
      ) : state === "playing" ? (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
          <rect x="6" y="5" width="4" height="14" rx="1.2" />
          <rect x="14" y="5" width="4" height="14" rx="1.2" />
        </svg>
      ) : state === "error" ? (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <path d="M12 8v5m0 3h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ) : (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5.5v13l11-6.5-11-6.5Z" />
        </svg>
      )}
    </button>
  );
};

// Ek baar me itni calls. Poora page bhar ke aaya = aur bhi ho sakti hain.
const PAGE_SIZE = 100;

// Contacts tab wala hi search box, taaki dono tabs ek jaise dikhein.
const SearchBar = ({ value, onChange }) => (
  <div className={styles.searchWrap}>
    <svg className={styles.searchIcon} width="15" height="15" viewBox="0 0 24 24" fill="none">
      <circle cx="11" cy="11" r="6.5" stroke="#8a8a95" strokeWidth="1.8" />
      <path d="M16 16l4.5 4.5" stroke="#8a8a95" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
    <input
      className={styles.search}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search name or number"
      aria-label="Search recent calls"
    />
  </div>
);

const RecentsList = ({ onPick }) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [moreError, setMoreError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [scope, setScope] = useState("own");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  // Har keystroke pe request na jaye.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchPage = useCallback(async (offset, q) => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (q) params.set("q", q);
    const res = await fetch(`${API_BASE}/dialer/calls?${params.toString()}`, {
      headers: { Authorization: `Bearer ${authToken()}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || "Could not load recents");
    return data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMoreError(null);
    try {
      const data = await fetchPage(0, query);
      const page = data.calls || [];
      setCalls(page);
      setScope(data.scope || "own");
      // Adhoora page = list khatam, to button dikhane ki zaroorat hi nahi.
      setHasMore(page.length === PAGE_SIZE);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchPage, query]);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const data = await fetchPage(calls.length, query);
      const page = data.calls || [];
      // Offset-based paging me, agar beech me nayi call aa jaaye to purani row
      // dobara aa sakti hai — id se dedupe karke duplicate rows se bachte hain.
      setCalls((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        return [...prev, ...page.filter((c) => !seen.has(c.id))];
      });
      setHasMore(page.length === PAGE_SIZE);
    } catch (err) {
      setMoreError(err.message || "Could not load more");
    } finally {
      setLoadingMore(false);
    }
  }, [calls.length, fetchPage, loadingMore, query]);

  useEffect(() => {
    load();
  }, [load]);

  // Chupchaap sirf pehla page dobara lo aur maujooda rows update kar do —
  // spinner nahi chalta aur "Load more" se aayi purani rows bhi bachi rehti hain.
  const refreshQuietly = useCallback(async () => {
    try {
      const data = await fetchPage(0, query);
      const page = data.calls || [];
      if (!page.length) return;
      setCalls((prev) => {
        const fresh = new Map(page.map((c) => [c.id, c]));
        const seen = new Set(prev.map((c) => c.id));
        const updated = prev.map((c) => fresh.get(c.id) || c);
        const added = page.filter((c) => !seen.has(c.id));
        return added.length ? [...added, ...updated] : updated;
      });
    } catch (_) {}
  }, [fetchPage, query]);

  // Koi call chal rahi ho tabhi poll karo — warna final status ke liye user ko
  // khud reload karna padta hai.
  const hasLiveCall = calls.some(isInProgress);
  useEffect(() => {
    if (!hasLiveCall) return;
    const timer = setInterval(() => {
      if (!document.hidden) refreshQuietly();
    }, 15000);
    return () => clearInterval(timer);
  }, [hasLiveCall, refreshQuietly]);

  // Search badalne par purani list dikhti rehti hai — har keystroke pe spinner nahi aata.
  if (loading && !calls.length) {
    return (
      <div className={styles.page}>
        <SearchBar value={search} onChange={setSearch} />
        <div className={styles.center}>
          <span className={styles.spinner} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <SearchBar value={search} onChange={setSearch} />
        <div className={styles.center}>
          <p className={styles.muted}>{error}</p>
          <button type="button" className={styles.retry} onClick={load}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!calls.length) {
    return (
      <div className={styles.page}>
        <SearchBar value={search} onChange={setSearch} />
        <div className={styles.center}>
          <p className={styles.muted}>
            {query ? "No call matches that search" : "No calls yet"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <SearchBar value={search} onChange={setSearch} />
      <div className={styles.wrap}>
      {scope === "account" ? (
        <p className={styles.scopeNote}>Showing everyone’s calls on this account</p>
      ) : null}

      <ul className={styles.list}>
        {calls.map((c) => {
          const badge = isInProgress(c)
            ? LIVE_LABEL
            : STATUS_LABEL[c.status] || STATUS_LABEL.Notpickup;
          return (
            <li key={c.id} className={styles.row}>
              {/* Number pe click = number keypad me chala jata hai, call user dabata hai. */}
              <button
                type="button"
                className={styles.rowMain}
                onClick={() => onPick?.(c.phoneNumber)}
                title="Use this number"
              >
                <span className={`${styles.dot} ${styles[badge.cls]}`} />
                <span className={styles.info}>
                  <span className={styles.number}>{c.contactName || c.phoneNumber}</span>
                  <span className={styles.meta}>
                    {/* Naam upar chala gaya to number yahin dikhta hai. */}
                    {c.contactName ? `${c.phoneNumber} · ` : ""}
                    {badge.text}
                    {c.durationSeconds ? ` · ${fmtDuration(c.durationSeconds)}` : ""}
                  </span>
                  {(scope === "account" || c.callByName || c.isMine) && (
                    <span className={styles.calledByTag}>
                      Called by: {c.isMine ? (c.callByName ? `${c.callByName} (You)` : "You") : (c.callByName || "Team")}
                    </span>
                  )}
                </span>
                <span className={styles.whenWrap}>
                  <span className={styles.whenDate}>{fmtDate(c.startTime || c.createdAt)}</span>
                  <span className={styles.whenTime}>{fmtTime(c.startTime || c.createdAt)}</span>
                </span>
              </button>

              {c.hasRecording ? <RecordingButton callId={c.id} /> : null}
            </li>
          );
        })}

        {/* Load more — sirf tab jab pichla page poora bhara hua aaya ho. */}
        {hasMore ? (
          <li className={styles.moreWrap}>
            {moreError ? <p className={styles.moreError}>{moreError}</p> : null}
            {/* App ka standard CTA — white body + purple text. Loading pe
                khud Loader dikhata hai, isliye alag spinner ki zaroorat nahi. */}
            <AnimatedButton2
              label={moreError ? "Try again" : "Load more"}
              isLoading={loadingMore}
              onClick={loadMore}
              positionType="relative"
              bottom="0px"
              size="15px"
            />
          </li>
        ) : null}
      </ul>
      </div>
    </div>
  );
};

export default RecentsList;
